import { useState, useCallback, useMemo } from "react";
import Container from "../../../common/Container";
import memeService from "../../../../services/MemeService";
import "./Browse.css";
import BrowseTemplates from "./BrowseTemplates";
import ImageSelectorRouter from "./ImageSelectorCardDisplay/ImageSelectorRouter";
import BlueProgressBar from "../../../common/BlueProgressBar";
import BlueScrollBackToTopButton from "../../../common/BlueScrollBackToTopButton";
import PreloadTemplateService from "../../../../services/PreloadService/PreloadTemplateService";
import notificationService from "../../../../services/NotificationService";
import { useTranslation } from "react-i18next";
import useAsync from "../../../../services/useAsync";
import Fuse from "fuse.js";

const Browse = () => {
    const { t } = useTranslation("templatesPage");

    const [userSearchInput, setUserSearchInput] = useState("");
    const [searchButtonIsGray, setSearchButtonIsGray] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [filteredTemplates, setFilteredTemplates] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [statusMessage, setStatusMessage] = useState("");
    const [buttonIsHidden, setButtonIsHidden] = useState(false);
    const [metaData, setMetaData] = useState(null);
    const [isBackgroundBlur, setIsBackgroundBlur] = useState("");
    const [userSettings, setUserSettings] = useState(null);
    const [priceOptions, setPriceOptions] = useState(null);
    const fuse = useMemo(() => {
        const fuseOptions = {
            minMatchCharLength: 0,
            findAllMatches: true,
            keys: [
                "title",
                "uploader"
            ]
        };
        return new Fuse(templates, fuseOptions);
    }, [templates]);

    const {
        selectedTemplates,
        replaceSelectedTemplate,
        addFirstSelectedTemplate,
        deleteSelectedTemplate,
        loadSelectedTemplates,
        loadUploadedTemplates,
    } = PreloadTemplateService();

    const getPriceList = useCallback(async () => {
        try {
            const priceOptionsResponse = await memeService.getPriceOptions();
            setPriceOptions(priceOptionsResponse.priceOptions);
        } catch (error) {
            notificationService.setMessage(t("cannotGetPriceMessage"));
        }
    }, []);

    const loadUserSettings = useCallback(async () => {
        let settings = await memeService.getBroadcasterSettings();
        setUserSettings(settings);
    }, []);

    const loadImageLibrary = useCallback(async () => {
        let images = await memeService.searchImageLibrary();
        setTemplates(images.results);
        setFilteredTemplates(images.results);
    }, []);

    const loadPage = useCallback(async () => {
        await Promise.all([
            loadUploadedTemplates(),
            loadSelectedTemplates(),
            loadImageLibrary(),
            loadUserSettings(),
            getPriceList(),
        ]);

        setIsLoading(false);
    }, [loadUploadedTemplates, loadSelectedTemplates, loadImageLibrary]);

    useAsync(loadPage);

    const addTemplateToSelected = async () => {
        // Step 1: Hide button and Display message
        setButtonIsHidden(true);
        setStatusMessage(t("browsePageAddTemplate"));

        // Step 2: Add the new template
        const response = await addFirstSelectedTemplate(
            metaData,
            metaData.newPrice ? metaData.newPrice : "0",
        );

        // Step 3: Update the template with a thumbnailUrl so we can identify it
        if (response)
            replaceSelectedTemplate({
                ...response,
                thumbnailUrl: metaData.thumbnailUrl,
            });

        // Step 4: Reset display message
        setStatusMessage("");
        setButtonIsHidden(false);
    };

    const removeSelectedTemplate = async () => {
        // Step 1: Hide button and display message
        setButtonIsHidden(true);
        setStatusMessage(t("browsesPageUnselect"));

        // Step 2: Find the selected template and delete it
        await deleteSelectedTemplate(
            selectedTemplates.find(
                (selected) => metaData.thumbnailUrl === selected.thumbnailUrl,
            ),
        );

        // Step3 3: Reset display message
        setStatusMessage("");
        setButtonIsHidden(false);
    };

    const onSearchInput = useCallback(async (e) => {
        // Step 1: Safe extraction of the value
        const searchValue = e?.target?.value || "";

        // Step 2: set user input
        setUserSearchInput(searchValue);
    }, []);

    const searchItem = useCallback(() => {
        if (!userSearchInput) {
            setFilteredTemplates(templates)
            return
        }
        const results = fuse.search(userSearchInput).map((resultItem => resultItem.item));
        setFilteredTemplates(results);
    }, [userSearchInput, fuse, templates, setFilteredTemplates]);

    // Renders the image along with its associated metadata and selection buttons, allowing the user to interact with the image.
    let content = (
        <ImageSelectorRouter
            metaData={metaData}
            setMetaData={setMetaData}
            addTemplateToSelected={addTemplateToSelected}
            statusMessage={statusMessage}
            buttonIsHidden={buttonIsHidden}
            selectedTemplates={selectedTemplates}
            removeSelectedTemplate={removeSelectedTemplate}
            isBackgroundBlur={isBackgroundBlur}
            setIsBackgroundBlur={setIsBackgroundBlur}
            setButtonIsHidden={setButtonIsHidden}
            priceOptions={priceOptions}
        />
    );

    return (
        <div className="scrollableContainer">
            {content}
            <BlueScrollBackToTopButton />
            <Container
                isNoBackground={true}
                noAnimation={true}
                content={
                    <>
                        <div>
                            <BlueProgressBar
                                title={t("browesPageSelectedMemesDisplay")}
                                max={
                                    userSettings
                                        ? userSettings.storageLimit
                                        : 20
                                }
                                selected={
                                    selectedTemplates
                                        ? selectedTemplates.length
                                        : 0
                                }
                            />
                            <br />
                            <br />
                            <div className={isBackgroundBlur}>
                                <BrowseTemplates
                                    userSearchInput={userSearchInput}
                                    setUserSearchInput={setUserSearchInput}
                                    searchButtonIsGray={searchButtonIsGray}
                                    setSearchButtonIsGray={
                                        setSearchButtonIsGray
                                    }
                                    templates={filteredTemplates}
                                    isLoading={isLoading}
                                    setIsLoading={setIsLoading}
                                    setMetaData={setMetaData}
                                    onSearchInput={onSearchInput}
                                    selectedTemplates={selectedTemplates}
                                    searchItem={searchItem}
                                />
                            </div>
                        </div>
                    </>
                }
            />
        </div>
    );
};

export default Browse;
